import { useQuery } from "@apollo/client";
import { Alert } from "@mui/material";
import { ReactElement } from "react";

import {
  GetAudienceSearchUserFilterQuery,
  GetAudienceSearchUserFilterQueryVariables,
  GetSubaccountShortcodeQuery,
  GetSubaccountShortcodeQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { ButtonLink } from "~frontend/components/Link";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { GET_AUDIENCE_SEARCH_USER_FILTER } from "~frontend/graphql/audience";
import { GET_SUBACCOUNT_SHORTCODE } from "~frontend/graphql/shortcode";
import { useFlagRedirect } from "~frontend/hooks/useFlagRedirect";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";

import { AudienceSubscribersTable } from "./AudienceSubscriberTable";
import { AudienceSummary } from "./AudienceSummary";

function AudiencePageHeader({
  shortcode,
}: {
  shortcode: NonNullable<
    GetSubaccountShortcodeQuery["getSubaccount"]["shortcode"]
  >;
}): ReactElement {
  const isStaff = useCurrentUserIsStaff();
  const { subaccountLink } = useSubaccount()!;

  return (
    <FlexBox gap={4} justifyContent="space-between">
      {shortcode.state === "NEW" && isStaff && (
        <Alert severity="warning">This short code is NOT active.</Alert>
      )}
      <AudienceSummary shortcode={shortcode} />
      {shortcode.subscriberAudienceId && (
        <ButtonLink
          to={subaccountLink(
            `/audiences/${shortcode.subscriberAudienceId}/settings`
          )}
          variant="contained"
          color="tertiary"
        >
          Audience Settings
        </ButtonLink>
      )}
    </FlexBox>
  );
}

function AudiencePageContent({
  shortcode,
}: {
  shortcode: NonNullable<
    GetSubaccountShortcodeQuery["getSubaccount"]["shortcode"]
  >;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const {
    loading,
    error,
    data: userSearchFilterData,
  } = useQuery<
    GetAudienceSearchUserFilterQuery,
    GetAudienceSearchUserFilterQueryVariables
  >(GET_AUDIENCE_SEARCH_USER_FILTER, {
    variables: {
      input: {
        subaccountId: subaccount.id,
        audienceId: shortcode.subscriberAudienceId!,
      },
    },
  });

  return (
    <FlexBox flexDirection="column" gap={4} sx={{ width: "100%" }}>
      <AudiencePageHeader shortcode={shortcode} />
      <AsyncWrapper
        loading={loading}
        errors={error}
        data={userSearchFilterData}
      >
        {({ getAudienceSearchUserFilter }) => (
          <AudienceSubscribersTable
            shortcode={shortcode}
            userSearchFilter={getAudienceSearchUserFilter}
          />
        )}
      </AsyncWrapper>
    </FlexBox>
  );
}

export function AudiencesPage(): ReactElement {
  useFlagRedirect("useAudiencesV2Page", "/shortcode");

  const { subaccount, subaccountLink } = useSubaccount()!;

  useBreadcrumbs([
    {
      label: "Subscribers",
      path: subaccountLink("/audiences"),
    },
  ]);

  // temporary: once we have generic audiences, we will need to provide a list of all audiences this subaccount has
  // right now, we can assume there is only one audience per subaccount
  const {
    data: shortcodeData,
    loading,
    error,
    startPolling,
    stopPolling,
  } = useQuery<
    GetSubaccountShortcodeQuery,
    GetSubaccountShortcodeQueryVariables
  >(GET_SUBACCOUNT_SHORTCODE, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  return (
    <PageContainer>
      <PageHeader>Subscribers</PageHeader>
      <AsyncWrapper data={shortcodeData} loading={loading} errors={error}>
        {({ getSubaccount }) => {
          const shortcode = getSubaccount.shortcode;
          return (
            <FlexBox justifyContent="space-between" alignItems="center">
              {shortcode ? (
                <AudiencePageContent
                  shortcode={shortcode}
                  startPolling={startPolling}
                  stopPolling={stopPolling}
                />
              ) : (
                <div />
              )}
            </FlexBox>
          );
        }}
      </AsyncWrapper>
    </PageContainer>
  );
}
